import { useState, useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { hasGoogleApiKey, setGoogleApiKey, hasPromoCode, setPromoCode } from '../../api/client'

export default function ApiKeyBanner() {
  const navigate = useNavigate()
  const location = useLocation()
  const [hasAccess, setHasAccess] = useState(true) // Assume true initially to avoid flash
  const [apiKeyInput, setApiKeyInput] = useState('')
  const [promoCodeInput, setPromoCodeInput] = useState('')
  const [showInput, setShowInput] = useState(false)
  const [inputMode, setInputMode] = useState<'apikey' | 'promo'>('apikey')
  const [successState, setSuccessState] = useState<{ type: 'apikey' | 'promo'; countdown: number } | null>(null)

  useEffect(() => {
    setHasAccess(hasGoogleApiKey() || hasPromoCode())
  }, [])

  // Countdown timer for redirect
  useEffect(() => {
    if (successState && successState.countdown > 0) {
      const timer = setTimeout(() => {
        setSuccessState({ ...successState, countdown: successState.countdown - 1 })
      }, 1000)
      return () => clearTimeout(timer)
    } else if (successState && successState.countdown === 0) {
      // Redirect to home
      if (location.pathname !== '/') {
        navigate('/')
      }
      setSuccessState(null)
      setHasAccess(true)
    }
  }, [successState, navigate, location.pathname])

  const handleSaveApiKey = () => {
    if (apiKeyInput.trim()) {
      setGoogleApiKey(apiKeyInput.trim())
      setShowInput(false)
      setApiKeyInput('')
      setSuccessState({ type: 'apikey', countdown: 3 })
    }
  }

  const handleSavePromoCode = () => {
    if (promoCodeInput.trim()) {
      setPromoCode(promoCodeInput.trim())
      setShowInput(false)
      setPromoCodeInput('')
      setSuccessState({ type: 'promo', countdown: 3 })
    }
  }

  // Show success message with countdown
  if (successState) {
    return (
      <div className="bg-green-500/20 border-b border-green-500/30">
        <div className="max-w-6xl mx-auto px-4 py-3">
          <div className="flex items-center justify-center gap-3">
            <svg
              className="w-5 h-5 text-green-400 flex-shrink-0"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M5 13l4 4L19 7"
              />
            </svg>
            <span className="text-green-200 text-sm">
              {successState.type === 'promo' ? (
                <><strong>VIP Access Activated!</strong> Redirecting to home in {successState.countdown}s...</>
              ) : (
                <><strong>API Key Saved!</strong> Redirecting to home in {successState.countdown}s...</>
              )}
            </span>
          </div>
        </div>
      </div>
    )
  }

  if (hasAccess) return null

  return (
    <div className="bg-amber-500/20 border-b border-amber-500/30">
      <div className="max-w-6xl mx-auto px-4 py-3">
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <svg
              className="w-5 h-5 text-amber-400 flex-shrink-0"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
              />
            </svg>
            <span className="text-amber-200 text-sm">
              <strong>Access required</strong> - Enter a promo code or your own Google API key.
            </span>
          </div>

          {showInput ? (
            <div className="flex items-center gap-2">
              {inputMode === 'promo' ? (
                <>
                  <input
                    type="text"
                    value={promoCodeInput}
                    onChange={(e) => setPromoCodeInput(e.target.value)}
                    placeholder="Enter promo code"
                    className="px-3 py-1.5 bg-surface-dark rounded border border-border text-foreground text-sm w-48 focus:outline-none focus:border-primary"
                    autoFocus
                    onKeyDown={(e) => e.key === 'Enter' && handleSavePromoCode()}
                  />
                  <button
                    onClick={handleSavePromoCode}
                    className="px-3 py-1.5 bg-primary text-foreground text-sm rounded hover:bg-primary-light transition-colors"
                  >
                    Apply
                  </button>
                </>
              ) : (
                <>
                  <input
                    type="password"
                    value={apiKeyInput}
                    onChange={(e) => setApiKeyInput(e.target.value)}
                    placeholder="Paste your API key"
                    className="px-3 py-1.5 bg-surface-dark rounded border border-border text-foreground text-sm w-64 focus:outline-none focus:border-primary"
                    autoFocus
                    onKeyDown={(e) => e.key === 'Enter' && handleSaveApiKey()}
                  />
                  <button
                    onClick={handleSaveApiKey}
                    className="px-3 py-1.5 bg-primary text-foreground text-sm rounded hover:bg-primary-light transition-colors"
                  >
                    Save
                  </button>
                </>
              )}
              <button
                onClick={() => setShowInput(false)}
                className="px-3 py-1.5 text-text-secondary text-sm hover:text-foreground transition-colors"
              >
                Cancel
              </button>
            </div>
          ) : (
            <div className="flex items-center gap-3">
              <button
                onClick={() => {
                  setInputMode('promo')
                  setShowInput(true)
                }}
                className="px-3 py-1.5 bg-green-500 text-foreground text-sm font-medium rounded hover:bg-green-400 transition-colors"
              >
                Have a Promo Code?
              </button>
              <span className="text-text-muted text-sm">or</span>
              <button
                onClick={() => {
                  setInputMode('apikey')
                  setShowInput(true)
                }}
                className="px-3 py-1.5 bg-amber-500 text-black text-sm font-medium rounded hover:bg-amber-400 transition-colors"
              >
                Enter API Key
              </button>
              <a
                href="https://aistudio.google.com/apikey"
                target="_blank"
                rel="noopener noreferrer"
                className="text-amber-300 text-sm hover:text-amber-200 underline"
              >
                Get API Key
              </a>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
